import { Component, ElementRef, Input, OnDestroy, Optional, Self, OnInit, Output, EventEmitter, ViewChild, HostListener, DoCheck, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ControlValueAccessor, NgControl} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ColumnaTabla } from '../tabla/interfaces';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpService } from '../http.service';
import { ENTER } from '@angular/cdk/keycodes';
import { ComponentesService } from '../componentes.service';

/** Interfaz para la implementacion de chips */
export interface CadaChip {
	name: string;
	_deAuto: boolean;
	autoValue: string;
	autoLabel: string;
	label: string;
}

export interface Destacado {
	atributo: string;
	clase: string;
}

export type IDestacado = Destacado | string;

/** Estructutra de datos generica para el autocomplete (ver si puede ser un string). */
export class Busca {
	constructor(
		public dato: any
	) {}
}

/**  `MatFormFieldControl` personalizado para la entrada autocomplete. */
@Component({
// tslint:disable-next-line: component-selector
	selector: 'tr2-autocomplete',
	templateUrl: 'autocomplete.component.html',
	styleUrls: ['autocomplete.component.css'],
	providers: [
		{ provide: MatFormFieldControl, useExisting: Autocomplete }
	],
	/** Definimos atributos para el html del componente */
	host: {
		'[class.autocomplete-floating]': 'shouldLabelFloat',
		'[id]': 'id',
		'[attr.aria-describedby]': 'describedBy'
	}
})
export class Autocomplete implements MatFormFieldControl<Busca>, OnDestroy, ControlValueAccessor, OnInit, AfterViewInit, DoCheck {

	/** Variable de resultados especificada desde el componente padre
	*  Si está seteada, no se llamará al backend
	*/
	@Input()
	get resultados(): any[] {
		return this._resultados;
	}
	set resultados(value: any[]) {
		this._resultados = value;
		this.init();
		this.stateChanges.next();
	}

	/** Variable que asigna el atributo name al input */
	@Input()
	get name(): any[] {
		return this._name;
	}
	set name(value: any[]) {
		this._name = value;
		this.stateChanges.next();
	}

	/** Variable que setea el placeholder de la entrada */
	@Input()
	get placeholder(): string {
		return this._placeholder;
	}
	set placeholder(value: string) {
		this._placeholder = value;
		this.stateChanges.next();
	}

	/** Variable que indica si el campo es requerido para el formulario */
	@Input()
	get required(): boolean {
		if (this._required) {
			return this._required;
		}

		// cuando el control retorna un error desde la validación con un valor vacío
		if (this.ngControl && this.ngControl.control && this.ngControl.control.validator) {
			const emptyValueControl = Object.assign({}, this.ngControl.control);
			(emptyValueControl as any).value = null;
			return 'required' in (this.ngControl.control.validator(emptyValueControl) || {});
		}
		return false;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}

	/** Variable para setear si el campo de entrada está deshabilitado o no */
	@Input()
	get disabled(): boolean {
		if (this.ngControl && this.ngControl.control && this.ngControl.control.disabled) {
			this._disabled = this.ngControl.control.disabled;
		}
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.ngControl.control.disable() : this.ngControl.control.enable();
		this.stateChanges.next();
	}

	/** Variable en donde se captura el valor de la entrada */
	@Input()
	get value(): any {
		return this.control.value;
	}
	set value(val: any) {
		this.control.setValue(val);
		this.stateChanges.next();
	}

	/** Función que indica si la entrada del formulario esta vacía */
	get empty() {
		return !this.control || this.control.value === null || this.control.value === '';
	}

	/** Función que indica si la etiqueta de la entrada pasa a flotante o no */
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}


	/** Constructor de la clase */
	constructor(
		fb: FormBuilder,
		private httpService: HttpService,
		private fm: FocusMonitor,
		private elRef: ElementRef<HTMLElement>,
		private componentesService: ComponentesService,
		@Optional() @Self() public ngControl: NgControl,
	) {
		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		fm.monitor(elRef, true).subscribe(origin => {
			this.focused = !!origin;
			this.stateChanges.next();
		});
	}

	/****** Definimos variables de clase ******/
	static nextId = 0;
	request = null;
	form: FormGroup;
	resultadosFiltrados: any[];
	stateChanges = new Subject<void>();
	focused = false;
	errorState = false;
	controlType = 'tr2-autocomplete';
	id = `tr2-autocomplete-${Autocomplete.nextId++}`;
	describedBy = '';
	control = new FormControl(null);
	readonly = false;
	isSimpleTextField = false;

	listaChips: CadaChip[] = [];
	selectable = true;
	removable = true;
	separatorKeysCodes: number[] = [ENTER];
	hasFocus = false;

	/**
	*	Primer argumento "que" de método autocomplete en HttpService
	*	Por ej: 'cuentascorrientes'
	*/
	@Input() busca: string;

	/** Parámetros extra al realizar la petición */
	@Input() buscaExtra = {};

	/** Variable que indica la cantidad minima para que ejecute la llamada al backend */
	@Input() min = 1;

	/** Variable que permite configurar la espera en la entrada de datos */
	@Input() esperar = 400;

	/** Si utiliza atributo autofocus */
	@Input() autoFocus = false;

	/** tabindex, no utiliza directiva FocusNext */
	@Input() tab = -1;

	/** Convertir el autocomplete en chips */
	@Input() chips = false;

	@Input() destacar: IDestacado[] = [];

	/** Permite seleccionar automáticamente el primer registro */
	@Input() autoActiveFirstOption: Boolean = true;

	/** Resultados (opciones) como tabla */
	@Input() table: ColumnaTabla[] = null;

	/** Classlist que se aplican al overlay container del mat-autocomplete */
	@Input() classList = '';

	/**
	*	Función personalizada que devuelve los datos al autocomplete
	*
	*	* IMPORTANTE: Debe devolver objetos que al menos tengan atributo 'label' y 'value'
	*/
	@Input() customSearch: Function;
	private _resultados: any[];
	private _name: any[];
	private _placeholder: string;
	private _required = false;
	private _disabled = false;

	/** Emite evento al cambiar los chips */
	@Output() chipChanges = new EventEmitter();

	/** Emite evento con el objeto traido del backend al seleccionar una opción */
	@Output() establecido = new EventEmitter();

	/** Emite evento al irse por blur */
	@Output() blur = new EventEmitter();

	/** Emite evento al realizar click en boton de sufijo */
	@Output() clickBoton = new EventEmitter();

	/** Botón extra que se pone como sufijo en input (icono) */
	@Input() boton = '';

	/** Permite configurar como se muestran las opciones de resultados */
	@Input() nombreResultado = 'label';

	/** Permite configurar como se muestran la opción seleccionada */
	@Input() nombreResultadoSeleccionado = 'label';

	@ViewChild('input', { static: false }) inputElement: ElementRef;

	@ViewChild('limpiarBtn', { static: false }) limpiarBtn: MatButton;

	// @ViewChild('autocomplete') matAutocomplete: MatAutocomplete;

	@ViewChild(MatAutocompleteTrigger, { static: false }) matAutocompleteTrigger: MatAutocompleteTrigger;

	// @ViewChild(TableOptionsComponent) options: TableOptionsComponent;

	/****** Variables de entrada ******/
	/** Variable que permite cambiar el valor a mostrar de la opción seleccionada */
	@Input() displayFn: (value: any) => string = (val) => val ? val[this.nombreResultadoSeleccionado] : '';

	/****** Métodos de clase ******/
	ngOnInit() {
		this.init();
	}

	init() {
		// setemos y enlazamos el formControl al del input del autocomplete
		this.control = this.ngControl.control as FormControl;

		// si no tengo ni url ni resultados, funcionará como un campode texto común
		if ((!this.busca || this.busca == '') && (!this.resultados || this.resultados.length <= 0)) {
			this.isSimpleTextField = true;
		} else {
			this.isSimpleTextField = false;
		}

		// controlamos si tenemos resultados y que el mismo sea un array de objetos
		if (this.resultados && this.resultados.length !== 0) {
			if (typeof this.resultados[0] !== 'object') {
				this.resultados.forEach((value, index) => {
					const obj_temp: Object = {} ;
					obj_temp[this.nombreResultado] = value;
					this.resultados[index] = obj_temp;
				});
			}
		}

		// si tenemos pocos resultados locales, podemos mosrtarlos cuando se hace foco
		if (this.resultados && this.resultados.length > 0 && (this.resultados.length < 10 || this.min == 0)) {
			this.min = 0;
			this.resultadosFiltrados = this.resultados;
		}

		if (typeof this.control.value === 'object' && this.control.value !== null) {
			// si tiene algo seleccionado porque se trajo el dato desde back por ejemplo y no esta desactivado
			if (!this.disabled) {
				this.readonly = true;
			}
		}

		// registramos el evento de entrada de datos para la búsqueda y filtrado
		this.control.valueChanges.pipe(debounceTime(this.esperar)).subscribe((value) => {
			if (value == null || value.length === 0) {
				this.limpiar();
			} else {
				if (typeof this.control.value === 'object' && this.control.value !== null) {
					// esta escribiendo y hay algo seleccionado, entonces mostramos boton de limpiar (readonly)
					this.readonly = true;
				}
				this._buscar(value);
			}
		});
	}

	ngAfterViewInit() {
	}

	ngOnDestroy() {
		this.stateChanges.complete();
		this.fm.stopMonitoring(this.elRef);
	}

	setDescribedByIds(ids: string[]) {
		this.describedBy = ids.join(' ');
	}

	/// tal vez deberia ser mas especifico que solo input?
	onContainerClick(event: MouseEvent) {
		if ((event.target as Element).tagName.toLowerCase() != 'input') {
			this.elRef.nativeElement.querySelector('input').focus();
		}
	}

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	writeValue() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnChange() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnTouched() { }

	/** Funcion que realiza la búsqueda en array de resultados o en backend si es remota */
	private _buscar(valor: any): void {
		// limpiamos siempre que entre
		this.resultadosFiltrados = [];

		// si es un simple campo de texto, no buscamos nada
		if (this.isSimpleTextField) {
			return;
		}
		if (!this.resultados || this.resultados.length === 0) {

			if (typeof valor === 'string' && valor.length >= this.min /* && this.hasFocus */) {
				// se procede a llamara al backend
				this.request = true;
				if (this.customSearch) {
					this.customSearch(valor).then(data => {
						this.request = null;
						if (!this.hasFocus) {
							this.matAutocompleteTrigger.closePanel();
							console.log('llego.... pero ya te fuiste');

							// si ya perdí en foco, puede ser que sea algo rapido, selecciono el primero
							if (data && data.length > 0 && this.autoActiveFirstOption) {
								this.establecer({
									option: {
										value: data[0]
									}
								});
							}
						}
						this.resultadosFiltrados = data;
					});
				} else {
					const tt = this.httpService.autocomplete(this.busca, valor, this.buscaExtra).then(data => {
						this.request = null;
						if (!this.hasFocus) {
							this.matAutocompleteTrigger.closePanel();
							console.log('llego.... pero ya te fuiste');

							// si ya perdí en foco, puede ser que sea algo rapido, selecciono el primero
							if (data && data.length > 0 && this.autoActiveFirstOption) {
								this.establecer({
									option: {
										value: data[0]
									}
								});
							}
						}
						this.resultadosFiltrados = data;
					});
				}
			}
		} else {
			if (typeof valor === 'string' && valor.length >= this.min) {
				let texto = valor.toLowerCase();
				if (this.chips) {
					texto = '^' + texto;
				}
				const re = new RegExp(texto);
				this.resultadosFiltrados = this.resultados.filter((val) => {
					const label = (typeof val === 'string') ? val.toLowerCase() : val[this.nombreResultado].toLowerCase();
					return re.test(label);
				});
			} else {
				this.resultadosFiltrados = [];
			}
		}
	}

	establecer(ev) {
		const resultado: any = ev.option.value;
		if ((this.isValid() || typeof resultado === 'object') && resultado !== undefined) {
			if (this.chips) {
				if (resultado && resultado[this.nombreResultado]) {
					this.listaChips.push({
						name: resultado[this.nombreResultado].trim(),
						_deAuto: (resultado['_deAuto'] != undefined && resultado['_deAuto'] != null) ? resultado['_deAuto'] : true,
						autoValue: resultado['value'],
						autoLabel: resultado[this.nombreResultado].trim(),
						label: resultado[this.nombreResultado].trim()
					});
					this.inputElement.nativeElement.value = '';
				}
				this.chipChanges.emit(this.listaChips);
			} else {
				if (resultado) {
					if (resultado && !this.readonly) {
						// sobrescribimos el control por si no lo completo o fue seleccionado por un click
						this.control.patchValue(resultado);
						this.establecido.emit(resultado);
					}
					this.readonly = true;
				}
			}
		}
	}

	isValid() {
		return !!this.control.value && (typeof this.control.value === 'object' || this.isSimpleTextField);
	}

	onClickBoton(event) {
		event.preventDefault();
		event.stopPropagation();
		this.clickBoton.emit(event);
		return false;
	}

	limpiar() {
		setTimeout(() => {
			this.control.reset(null, {emitEvent: false});
			this.readonly = false;
			this.resultadosFiltrados = [];
			this.listaChips = [];
			this.establecido.emit(this.control.value);
			this.stateChanges.next();
		});
	}

	focus() {
		if (this.readonly) {
			this.limpiarBtn.focus();
		} else {
			this.inputElement.nativeElement.focus();
		}
	}

	onFocus(event) {
		this.hasFocus = true;
		if (event.target.select) {
			event.target.select();
		}
		if (this.resultadosFiltrados != undefined && this.resultadosFiltrados.length === 0 && this.min == 0) {
			this._buscar('');
		}
	}

	onBlur(ev) {
		if (ev && ev.relatedTarget && ev.relatedTarget.id.includes('mat-option')) {
			const index = Number.parseInt(ev.relatedTarget.id.substring(ev.relatedTarget.id.lastIndexOf('-') + 1, ev.relatedTarget.id.length), 10);
			if (!this.chips) {
				this.establecer({
					option: {
						value: this.resultadosFiltrados[index]
					}
				});
			}
		} else {
			setTimeout(() => {
				let cond: boolean;
				cond = !this.control.value || this.control.value.length === 0;
				if (!this.isValid()) {
					if (!cond) {
						this.limpiar();
						this.componentesService.notification(`No se seleccionó ninguna opción para ${this.placeholder}`);
						if (this.required) {
							this.inputElement.nativeElement.focus();
						}
					}
				}
			}, 5000);
			if (this.min > 0) {
				this.resultadosFiltrados = [];
			}
		}
		this.setTouched();
		this.blur.emit();
		this.hasFocus = false;

		if (!this.isSimpleTextField) {
			if ((!this.autoActiveFirstOption && this.control.value && typeof this.control.value === 'string') || (this.autoActiveFirstOption && this.control.value && typeof this.control.value === 'string' && this.matAutocompleteTrigger.panelOpen && !this.table)) {
				// en el caso de que no se seleccionó ninguno y no es automatico,
				// limpio el control
				this.control.patchValue(null);
			}
			setTimeout(() => {
				this.matAutocompleteTrigger.closePanel();
			}, 250);
		}
	}

	setTouched(): void {
		if (!this.control.touched) {
			this.control.markAsTouched();
			this.errorState = this.control.invalid;
		}
		this.stateChanges.next();
	}

	sacaChip(chip: CadaChip): void {
		const index: number = this.listaChips.indexOf(chip);
		if (index !== -1) {
			this.listaChips.splice(index, 1);
		}
		this.chipChanges.emit(this.listaChips);
		if (this.listaChips.length == 0) {
			this.limpiar();
		}
	}

	agregaChip(event: MatChipInputEvent): void {
		// Add fruit only when MatAutocomplete is not open
		// To make sure this does not conflict with OptionSelected Event
		/*
		if (!this.matAutocomplete.isOpen) {
		*/
		const input = event.input;
		const value = event.value;
		if ((value || '').trim()) {
			const last: any = this.listaChips[this.listaChips.length - 1 ];
			if (last && last._deAuto) {
				last._deAuto = false;
				last.name = value.trim();
				last.label = last.autoLabel + ':' +  value.trim();
			} else {
				this.listaChips.push({
					name: value.trim(),
					_deAuto: false,
					autoLabel: '',
					autoValue: '',
					label: value.trim()
				});
			}
			this.chipChanges.emit(this.listaChips);
		}
		// Reset the input value
		if (input) {
			input.value = '';
		}
	}

	destacable(resultado: any) {
		const clases = [];
		if (this.destacar && this.destacar.length > 0) {
			for (let i = 0; i < this.destacar.length; i++) {
				if (typeof this.destacar[i] === 'string') {
					const clase: string = this.destacar[i] as string;
					if (resultado.hasOwnProperty(this.destacar[i]) && resultado[clase]) {
						clases.push(clase);
					}
				} else {
					const destacar: Destacado = this.destacar[i] as Destacado;
					if (resultado.hasOwnProperty(destacar.atributo) && resultado[destacar.atributo]) {
						clases.push(destacar.clase);
					}
				}
			}
		}
		return clases;
	}

	ngDoCheck() {
		this.errorState = this.ngControl.invalid && (this.ngControl.touched || this.ngControl.dirty);
	}

	limpiaChips() {
		this.listaChips = [];
		this.limpiar();
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (event.keyCode == 27) {
			event.preventDefault();
			event.stopPropagation();
			this.limpiar();
		}
		if (event.keyCode == 13 && this.isSimpleTextField) {
			event.preventDefault();
			event.stopPropagation();
			this.establecido.emit(this.value);
		}
		if (event.keyCode == ENTER) {
			setTimeout(() => {
				if (this.chips && !this.focused) {
					// le damos foco para que no se nos vaya por el trfocusnext por si queremos seguir aplicando chips
					this.focus();
				}
			}, 50);
		}
		/* if (this.options) {
			if (event.keyCode == ENTER) {
				this.establecer({
					option: {
						value: this.options.seleccionado
					}
				});
			}
			if (event.keyCode == UP_ARROW) {
				this.options.moverCursor(1);
			} else if (event.keyCode == DOWN_ARROW) {
				this.options.moverCursor(2);
			}
		} */
	}
}

